import { Link } from 'wouter';
import { Section } from '@/components/layout/Section';
import { PageHero } from '@/components/brand/PageHero';
import { Button } from '@/components/ui/Button';
import { FeaturedWork } from '@/components/ui/FeaturedWork';
import { services } from '@/content/services';
import { serviceDetails } from '@/content/serviceDetails';
import { usePageMeta } from '@/lib/usePageMeta';

const deliverySteps = [
  { title: 'Understand', body: 'Start with the problem, your users and the systems you already have.' },
  { title: 'Agree the scope', body: 'Confirm deliverables, costs, milestones and what success looks like in writing.' },
  { title: 'Build and verify', body: 'Review working software as it develops and test the journeys that matter.' },
  { title: 'Launch and hand over', body: 'Receive the code, documentation and an agreed plan for support.' },
];

export default function Services() {
  usePageMeta(
    'Custom Software & App Development Services',
    'Explore custom web and mobile development, AI and data solutions, QA testing and workflow automation. Find the right service and see related work from Falcon Solutions.',
  );

  return (
    <>
      <PageHero compact label="Our services" headline="What would you like to build or improve?"
        description="Launch a product, make information useful, or connect the tools your business runs on. Choose the work you need done and explore how we can help."
        action="Explore our services" target="#service-list" note="A written scope. Direct access to the engineers. A practical handover." />

      <Section id="service-list" size="sm" className="scroll-mt-24">
        <div className="mb-10 max-w-[65ch]">
          <h2 className="text-display-md">Find your starting point.</h2>
          <p className="mt-5 leading-relaxed text-ink/70">Each service starts with a business need. Explore typical projects below, then open a service for its approach, deliverables and common questions.</p>
        </div>
        <div className="grid gap-5 md:grid-cols-2">
          {services.map((service) => {
            const detail = serviceDetails[service.slug];
            return (
              <article key={service.slug} id={service.slug} className="scroll-mt-24">
                <Link href={`/services/${service.slug}`} aria-labelledby={`overview-${service.slug}`}
                  className="group flex h-full flex-col rounded-2xl border border-hairline bg-surface-muted p-6 transition-colors duration-200 hover:border-brand hover:bg-surface-blue sm:p-8">
                  <div className="flex items-start justify-between gap-5">
                    <h3 id={`overview-${service.slug}`} className="font-display text-2xl font-bold leading-tight [font-stretch:100%]">{service.name}</h3>
                    <span aria-hidden="true" className="grid h-10 w-10 shrink-0 place-items-center rounded-full bg-white text-xl text-brand transition-colors group-hover:bg-brand group-hover:text-white">↗</span>
                  </div>
                  <p className="mt-4 max-w-[58ch] text-sm leading-relaxed text-ink/75">{detail.audience}</p>
                  <p className="mt-6 text-sm font-semibold">Typical projects</p>
                  <ul className="mt-3 flex-1 space-y-2 text-sm leading-relaxed text-ink/75">
                    {detail.examples.map((example) => <li key={example} className="flex gap-3"><span aria-hidden="true" className="mt-2 h-1 w-1 shrink-0 rounded-full bg-brand" /><span>{example}</span></li>)}
                  </ul>
                  <span className="mt-7 border-t border-hairline pt-4 text-sm font-semibold text-brand">Explore this service<span className="sr-only">: {service.name}</span></span>
                </Link>
              </article>
            );
          })}
        </div>
      </Section>

      <Section tone="blue" size="sm">
        <div className="max-w-[60ch]"><h2 className="text-display-md">A clear path from idea to handover.</h2><p className="mt-5 leading-relaxed text-ink/75">Whether you need one service or several, the delivery process keeps the work visible and the next step clear.</p></div>
        <ol className="mt-10 grid gap-8 sm:grid-cols-2 lg:grid-cols-4">
          {deliverySteps.map((step, index) => <li key={step.title} className="border-t border-brand/25 pt-5"><p className="text-sm font-medium text-brand">Step {index + 1}</p><h3 className="mt-3 font-display text-xl font-bold [font-stretch:100%]">{step.title}</h3><p className="mt-3 text-sm leading-relaxed text-ink/75">{step.body}</p></li>)}
        </ol>
      </Section>

      <FeaturedWork />

      <Section tone="dark" size="sm">
        <div className="flex flex-col items-start justify-between gap-8 lg:flex-row lg:items-center">
          <div><h2 className="text-display-md max-w-[23ch]">Not sure where your project fits?</h2><p className="mt-5 max-w-[60ch] leading-relaxed text-white/75">Tell us what is slowing your team down or what you want to build. We will help identify the right service, including when a simpler solution would do.</p></div>
          <Button href="/contact" variant="inverse" size="lg" className="shrink-0">Discuss your project</Button>
        </div>
      </Section>
    </>
  );
}
